import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, SearchableDropdown, SideBarModal } from '@/components';
import { Repossession, RepossessionSeverity, Handover } from '@/types/common';
import { getListHandover } from '@/services/api/handovers';
import { getListUser, User } from '@/services/api/users';
import { getRepossessionById } from '@/services/api/repossessions';

export interface RepoFormData {
  handoverID?: string;
  platNomor: string;
  clientID: string;
  clientName: string;
  pitstopID: string;
  pitstopName: string;
  mitraCode: string;
  mitraName: string;
  severity: RepossessionSeverity;
  reason: string;
  assignedOpsTeam: string;
  notes: string;
}

type RepoFormMode = 'create' | 'edit' | 'view';

interface RepoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RepoFormData) => void;
  isLoading?: boolean;
  mode: RepoFormMode;
  repoId?: string;
  initialData?: Repossession;
}

const useRepoFormData = () => {
  const [handovers, setHandovers] = useState<Handover[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [handoversResponse, usersResponse] = await Promise.all([
        getListHandover({
          page: '1',
          size: '999',
          statuses: 'ACTIVE',
          isAvailable: 'true',
        }),
        getListUser({ page: '1', size: '999' }),
      ]);
      setHandovers(handoversResponse.data || []);
      setUsers(usersResponse.data || []);
    } catch (error) {
      console.error('Error loading form data:', error);
      setHandovers([]);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { handovers, users, isLoading, loadData };
};

const useRepoDetail = (repoId?: string) => {
  const [repoDetail, setRepoDetail] = useState<Repossession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    if (!id) return;

    setIsLoading(true);
    setError(null);
    try {
      const detail = await getRepossessionById(id);
      setRepoDetail(detail);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch repossession details';
      setError(errorMessage);
      console.error('Error fetching repossession detail:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearDetail = useCallback(() => {
    setRepoDetail(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (repoId) {
      fetchDetail(repoId);
    } else {
      clearDetail();
    }
  }, [repoId, fetchDetail, clearDetail]);

  return {
    repoDetail,
    isLoading,
    error,
    refetch: () => repoId && fetchDetail(repoId),
  };
};

const RepoModal: React.FC<RepoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  mode,
  repoId,
  initialData,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<RepoFormData>({
    handoverID: '',
    platNomor: '',
    clientID: '',
    clientName: '',
    pitstopID: '',
    pitstopName: '',
    mitraCode: '',
    mitraName: '',
    severity: RepossessionSeverity.MEDIUM,
    reason: '',
    assignedOpsTeam: '',
    notes: '',
  });

  const useFormValidation = (currentMode: RepoFormMode) => {
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const validateField = useCallback(
      (field: string, value: any): string | null => {
        switch (field) {
          case 'handoverID':
            if (!value && currentMode === 'create') {
              return 'Handover selection is required';
            }
            break;
          case 'severity':
            if (!value) {
              return 'Severity is required';
            }
            break;
          case 'reason':
            if (!value) {
              return 'Reason is required';
            } else if (value.length < 15) {
              return 'Reason must be at least 15 characters';
            }
            break;
          case 'assignedOpsTeam':
            if (!value) {
              return 'Assigned team member is required';
            }
            break;
        }
        return null;
      },
      [currentMode]
    );

    const validateForm = useCallback(
      (formData: RepoFormData): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.handoverID && currentMode === 'create') {
          newErrors.handoverID = 'Handover selection is required';
        }
        if (!formData.severity) {
          newErrors.severity = 'Severity is required';
        }
        if (!formData.reason) {
          newErrors.reason = 'Reason is required';
        } else if (formData.reason.length < 15) {
          newErrors.reason = 'Reason must be at least 15 characters';
        }
        if (!formData.assignedOpsTeam) {
          newErrors.assignedOpsTeam = 'Assigned team member is required';
        }

        setErrors(newErrors);
        setTouched({
          handoverID: true,
          severity: true,
          reason: true,
          assignedOpsTeam: true,
        });
        return Object.keys(newErrors).length === 0;
      },
      [currentMode]
    );

    // Simplified: Single function to handle field blur (touch + validate)
    const handleFieldBlur = useCallback(
      (field: string, value: any) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        const error = validateField(field, value);
        setErrors((prev) => ({ ...prev, [field]: error || '' }));
      },
      [validateField]
    );

    const clearError = useCallback((field: string) => {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }, []);

    const clearAllErrors = useCallback(() => {
      setErrors({});
      setTouched({});
    }, []);

    return {
      errors,
      touched,
      validateForm,
      handleFieldBlur,
      clearError,
      clearAllErrors,
    };
  };

  const {
    handovers,
    users,
    isLoading: isLoadingFormData,
    loadData,
  } = useRepoFormData();
  const {
    repoDetail,
    isLoading: isLoadingDetail,
    error: detailError,
    refetch,
  } = useRepoDetail(mode === 'edit' || mode === 'view' ? repoId : undefined);
  const {
    errors,
    touched,
    validateForm,
    handleFieldBlur,
    clearError,
    clearAllErrors,
  } = useFormValidation(mode);

  const currentRepoData = repoDetail || initialData;

  const isReadOnly = mode === 'view' || (mode === 'edit' && !isEditing);
  const isViewMode = mode === 'view' || (mode === 'edit' && !isEditing);
  const showSubmitButton = mode === 'create' || (mode === 'edit' && isEditing);
  const showEditButton = mode === 'edit' && !isEditing;

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    const needsHandover = mode === 'create';
    return (
      (!needsHandover || !!formData.handoverID) &&
      !!formData.severity &&
      !!formData.reason &&
      formData.reason.length >= 15 &&
      !!formData.assignedOpsTeam
    );
  }, [formData, mode]);

  const selectedHandover = useMemo(
    () => handovers.find((h) => h.ID === formData.handoverID),
    [handovers, formData.handoverID]
  );

  const handoverOptions = useMemo(
    () =>
      handovers
        ?.filter((h) => h?.ID)
        ?.map((h) => ({
          value: h.ID,
          label: `${h?.platNomor} - ${h?.oemName} - ${h?.modelName}`,
          data: h,
        })) || [],
    [handovers]
  );

  const userOptions = useMemo(
    () =>
      users
        ?.filter((u) => u?.email)
        ?.map((u) => ({
          value: u.email,
          label: u.email,
          data: u,
        })) || [],
    [users]
  );

  const getModalTitle = () => {
    if (mode === 'create') return 'Create Repo';
    if (mode === 'edit') return isEditing ? 'Edit Repo' : 'View Repo';
    return 'View Repo';
  };

  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      clearAllErrors();
      loadData();
    }
  }, [isOpen, clearAllErrors, loadData]);

  useEffect(() => {
    if (
      currentRepoData &&
      (mode === 'edit' || mode === 'view') &&
      handovers.length > 0
    ) {
      const matchingHandover = handovers.find(
        (h) =>
          h.vehicleID === currentRepoData.vehicleID ||
          h.platNomor === currentRepoData.platNomor
      );

      const newFormData = {
        handoverID: matchingHandover?.ID || '',
        platNomor: currentRepoData.platNomor || '',
        clientID: String(currentRepoData.clientID || ''),
        clientName:
          currentRepoData.clientName || matchingHandover?.clientName || '',
        pitstopID: String(
          (currentRepoData as any).hubID || currentRepoData.pitstopID || ''
        ),
        pitstopName:
          (currentRepoData as any).hubName || matchingHandover?.hubName || '',
        mitraCode: currentRepoData.driverCode || '',
        mitraName: currentRepoData.driverName || '',
        severity: currentRepoData.severity || RepossessionSeverity.MEDIUM,
        reason: currentRepoData.reason || '',
        assignedOpsTeam:
          currentRepoData.assignedOpsTeam || currentRepoData.assignedTo || '',
        notes: currentRepoData.notes || '',
      };

      setFormData(newFormData);
    } else if (
      currentRepoData &&
      (mode === 'edit' || mode === 'view') &&
      handovers.length === 0
    ) {
      const basicFormData = {
        handoverID: '',
        platNomor: currentRepoData.platNomor || '',
        clientID: String(currentRepoData.clientID || ''),
        clientName: currentRepoData.clientName || '',
        pitstopID: String(
          (currentRepoData as any).hubID || currentRepoData.pitstopID || ''
        ),
        pitstopName: (currentRepoData as any).hubName || '',
        mitraCode: currentRepoData.driverCode || '',
        mitraName: currentRepoData.driverName || '',
        severity: currentRepoData.severity || RepossessionSeverity.MEDIUM,
        reason: currentRepoData.reason || '',
        assignedOpsTeam:
          currentRepoData.assignedOpsTeam || currentRepoData.assignedTo || '',
        notes: currentRepoData.notes || '',
      };
      setFormData(basicFormData);
    } else if (mode === 'create') {
      setFormData({
        handoverID: '',
        platNomor: '',
        clientID: '',
        clientName: '',
        pitstopID: '',
        pitstopName: '',
        mitraCode: '',
        mitraName: '',
        severity: RepossessionSeverity.MEDIUM,
        reason: '',
        assignedOpsTeam: '',
        notes: '',
      });
    }
  }, [currentRepoData, mode, handovers]);

  const handleFieldChange = useCallback(
    (field: keyof RepoFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));

      // If field has been touched, validate it in real-time
      if (touched[field]) {
        handleFieldBlur(field, value);
      } else {
        // Just clear error if field hasn't been touched yet
        clearError(field);
      }
    },
    [touched, handleFieldBlur, clearError]
  );

  const handleHandoverSelect = useCallback(
    (handoverID: string) => {
      if (!handoverID) {
        setFormData((prev) => ({
          ...prev,
          handoverID: '',
          platNomor: '',
          clientID: '',
          clientName: '',
          pitstopID: '',
          pitstopName: '',
          mitraCode: '',
          mitraName: '',
        }));
        return;
      }

      const selectedHandover = handovers.find((h) => h.ID === handoverID);
      if (selectedHandover) {
        setFormData((prev) => ({
          ...prev,
          handoverID,
          platNomor: selectedHandover.platNomor || '',
          clientID: selectedHandover.clientID || '',
          clientName: selectedHandover.clientName || '',
          pitstopID: selectedHandover.pitstopID || selectedHandover.hubId || '',
          pitstopName: selectedHandover.hubName || '',
          mitraCode: selectedHandover.driverCode || '',
          mitraName: selectedHandover.driverName || '',
        }));
        [
          'handoverID',
          'platNomor',
          'clientID',
          'pitstopID',
          'mitraCode',
          'mitraName',
        ].forEach(clearError);
      }
    },
    [handovers, clearError]
  );

  const handleSubmit = useCallback(() => {
    if (validateForm(formData)) {
      onSubmit(formData);
    }
  }, [validateForm, formData, onSubmit]);

  const handleClose = useCallback(() => {
    clearAllErrors();
    setIsEditing(false);

    setFormData({
      handoverID: '',
      platNomor: '',
      clientID: '',
      clientName: '',
      pitstopID: '',
      pitstopName: '',
      mitraCode: '',
      mitraName: '',
      severity: RepossessionSeverity.MEDIUM,
      reason: '',
      assignedOpsTeam: '',
      notes: '',
    });

    onClose();
  }, [clearAllErrors, onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKey);
    }

    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="right"
        width="sm"
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {getModalTitle()}
            </h2>
            <p className="text-sm font-normal text-gray-600">
              {mode === 'create'
                ? 'Create a new vehicle repossession.'
                : isEditing
                  ? 'Edit repossession details.'
                  : 'View repossession details.'}
            </p>
          </div>
        }
        footer={
          showEditButton ? (
            <Button
              variant="primary"
              onClick={() => setIsEditing(true)}
              className="w-full py-3"
            >
              Edit
            </Button>
          ) : showSubmitButton ? (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isLoading || !isFormValid}
              className="w-full py-3"
            >
              {isLoading
                ? 'Saving...'
                : mode === 'create'
                  ? 'Save'
                  : 'Update Repossession'}
            </Button>
          ) : (
            <Button
              variant="neutral"
              onClick={handleClose}
              className="w-full py-3"
            >
              Close
            </Button>
          )
        }
      >
        <div className="flex-1">
          {/* Error State for Detail Fetching */}
          {detailError && (mode === 'edit' || mode === 'view') && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{detailError}</p>
                  <button
                    onClick={refetch}
                    className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading State for Detail Fetching */}
          {isLoadingDetail && (mode === 'edit' || mode === 'view') && (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-4 text-sm text-gray-400">
                Loading repossession details...
              </p>
            </div>
          )}

          {/* Content - only show when not loading detail or when in create mode */}
          {(!isLoadingDetail || mode === 'create') && !detailError && (
            <>
              {isViewMode ? (
                <div>
                  {/* Plate Number + OEM/Model */}
                  <div className="px-6">
                    <div className="pt-6 pb-4 border-b border-gray-200">
                      <p className="heading-field mb-1">Plate Number</p>
                      <p className="text-sm font-medium text-black">
                        {formData.platNomor || '-'}
                      </p>
                    </div>
                    <div className="pt-4 pb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="heading-field mb-1">OEM Name</p>
                          <p className="text-sm font-medium text-black">
                            {selectedHandover?.oemName ||
                              currentRepoData?.oemName ||
                              '-'}
                          </p>
                        </div>
                        <div>
                          <p className="heading-field mb-1">Model Name</p>
                          <p className="text-sm font-medium text-black">
                            {selectedHandover?.modelName ||
                              currentRepoData?.modelName ||
                              '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Repo Detail section header */}
                  <div className="bg-gray-50 px-6 py-2 font-medium heading-field">
                    Repo Detail
                  </div>

                  {/* Repo Detail Content */}
                  <div className="px-6">
                    {/* Clients + Pitstop */}
                    <div className="pt-4 pb-4 border-b border-gray-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="heading-field mb-1">Clients</p>
                          <p className="text-sm font-medium text-black break-words">
                            {selectedHandover?.clientName ||
                              currentRepoData?.clientName ||
                              '-'}
                          </p>
                        </div>
                        <div>
                          <p className="heading-field mb-1">Pitstop</p>
                          <p className="text-sm font-medium text-black break-words">
                            {selectedHandover?.hubName ||
                              currentRepoData?.hubName ||
                              currentRepoData?.pitstop ||
                              '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Mitra Code + Mitra Name */}
                    <div className="pt-4 pb-4 border-b border-gray-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="heading-field mb-1">Mitra Code</p>
                          <p className="text-sm font-medium text-black break-words">
                            {selectedHandover?.driverCode ||
                              currentRepoData?.driverCode ||
                              '-'}
                          </p>
                        </div>
                        <div>
                          <p className="heading-field mb-1">Mitra Name</p>
                          <p className="text-sm font-medium text-black break-words">
                            {selectedHandover?.driverName ||
                              currentRepoData?.driverName ||
                              '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Severity */}
                    <div className="pt-4 pb-4 border-b border-gray-200">
                      <p className="heading-field mb-1">Severity</p>
                      <p className="text-sm font-medium text-black">
                        {formData.severity
                          ? formData.severity.charAt(0).toUpperCase() +
                            formData.severity.slice(1).toLowerCase()
                          : '-'}
                      </p>
                    </div>

                    {/* Reason */}
                    <div className="pt-4 pb-4 border-b border-gray-200">
                      <p className="heading-field mb-1">Reason</p>
                      <p className="text-sm font-medium text-black break-words whitespace-pre-wrap">
                        {formData.reason || '-'}
                      </p>
                    </div>

                    {/* Assign Repo Team */}
                    <div className="pt-4 pb-4 border-b border-gray-200">
                      <p className="heading-field mb-1">Assign Repo Team</p>
                      <p className="text-sm font-medium text-black break-words">
                        {formData.assignedOpsTeam || '-'}
                      </p>
                    </div>

                    {/* Notes */}
                    <div className="pt-4 pb-4">
                      <p className="heading-field mb-1">Notes</p>
                      <p className="text-sm font-medium text-black break-words whitespace-pre-wrap">
                        {formData.notes || '-'}
                      </p>
                    </div>
                  </div>

                  {/* Info section */}
                  {currentRepoData && (
                    <>
                      <div className="bg-gray-50 px-6 py-2 font-medium heading-field">
                        Info
                      </div>

                      <div className="px-6">
                        {/* Created By + Created Date */}
                        <div className="pt-4 pb-4 border-b border-gray-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="heading-field mb-1">Created By</p>
                              p=:{' '}
                              <p className="text-sm font-medium text-black break-words">
                                {currentRepoData.createdBy || '-'}
                              </p>
                            </div>
                            <div>
                              <p className="heading-field mb-1">Created Date</p>
                              <p className="text-sm font-medium text-black break-words">
                                {currentRepoData.createdAt
                                  ? new Date(
                                      currentRepoData.createdAt
                                    ).toLocaleDateString('id-ID') +
                                    ' at ' +
                                    new Date(
                                      currentRepoData.createdAt
                                    ).toLocaleTimeString('en-GB', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Last Updated By + Last Updated */}
                        <div className="pt-4 pb-6">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="heading-field mb-1">
                                Last Updated By
                              </p>
                              <p className="text-sm font-medium text-black break-words">
                                {currentRepoData.updatedBy || '-'}
                              </p>
                            </div>
                            <div>
                              <p className="heading-field mb-1">Last Updated</p>
                              <p className="text-sm font-medium text-black break-words">
                                {currentRepoData.updatedAt
                                  ? new Date(
                                      currentRepoData.updatedAt
                                    ).toLocaleDateString('id-ID') +
                                    ' at ' +
                                    new Date(
                                      currentRepoData.updatedAt
                                    ).toLocaleTimeString('en-GB', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-0">
                  <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 rounded-t-lg heading-field">
                    Detail
                  </div>
                  <div className="space-y-3 px-6 py-4">
                    {/* Handover Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plate Number <span className="text-red-500">*</span>
                      </label>

                      {isEditing ? (
                        <input
                          type="text"
                          value={
                            currentRepoData
                              ? `${currentRepoData.platNomor || ''} - ${(currentRepoData as any).oemName || ''} - ${(currentRepoData as any).modelName || ''}`
                              : formData.platNomor || '-'
                          }
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 cursor-not-allowed"
                        />
                      ) : (
                        <SearchableDropdown
                          options={handoverOptions}
                          value={formData.handoverID || ''}
                          onChange={handleHandoverSelect}
                          placeholder={
                            isLoadingFormData
                              ? 'Loading handovers...'
                              : 'Search and select handover...'
                          }
                          disabled={isReadOnly || isLoadingFormData}
                          error={!!(errors.handoverID && touched.handoverID)}
                          className="w-full"
                          emptyMessage="No handovers found"
                          allowClear
                        />
                      )}
                      {errors.handoverID &&
                        touched.handoverID &&
                        !isEditing && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.handoverID}
                          </p>
                        )}
                    </div>

                    {/* Auto-filled fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={
                          formData.clientName ||
                          selectedHandover?.clientName ||
                          '-'
                        }
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pitstop <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={
                          formData.pitstopName ||
                          selectedHandover?.hubName ||
                          '-'
                        }
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mitra
                      </label>
                      <input
                        type="text"
                        value={
                          formData.mitraCode && formData.mitraName
                            ? `${formData.mitraCode} - ${formData.mitraName}`
                            : selectedHandover?.driverCode &&
                                selectedHandover?.driverName
                              ? `${selectedHandover.driverCode} - ${selectedHandover.driverName}`
                              : '-'
                        }
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 cursor-not-allowed"
                      />
                    </div>

                    {/* Severity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Severity <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.severity}
                        onChange={(e) =>
                          handleFieldChange(
                            'severity',
                            e.target.value as RepossessionSeverity
                          )
                        }
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
                          errors.severity && touched.severity
                            ? 'border-red-500'
                            : 'border-gray-300'
                        } ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                      >
                        <option value={RepossessionSeverity.LOW}>Low</option>
                        <option value={RepossessionSeverity.MEDIUM}>
                          Medium
                        </option>
                        <option value={RepossessionSeverity.HIGH}>High</option>
                        <option value={RepossessionSeverity.CRITICAL}>
                          Critical
                        </option>
                      </select>
                      {errors.severity && touched.severity && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.severity}
                        </p>
                      )}
                    </div>

                    {/* Reason */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <textarea
                          value={formData.reason}
                          onChange={(e) =>
                            handleFieldChange('reason', e.target.value)
                          }
                          onBlur={(e) =>
                            handleFieldBlur('reason', e.target.value)
                          }
                          placeholder="Jelaskan alasan utama penarikan unit"
                          rows={3}
                          maxLength={200}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 resize-none ${
                            errors.reason && touched.reason
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <div className="absolute bottom-3 right-2 text-xs text-gray-400 pointer-events-none">
                          {formData.reason.length}/200
                        </div>
                      </div>
                      {errors.reason && touched.reason && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.reason}
                        </p>
                      )}
                    </div>

                    {/* Assigned Team */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assign Repo Team <span className="text-red-500">*</span>
                      </label>
                      <div
                        onBlur={() =>
                          handleFieldBlur(
                            'assignedOpsTeam',
                            formData.assignedOpsTeam
                          )
                        }
                      >
                        <SearchableDropdown
                          options={userOptions}
                          value={formData.assignedOpsTeam || ''}
                          onChange={(value) =>
                            handleFieldChange('assignedOpsTeam', value)
                          }
                          placeholder={
                            isLoadingFormData
                              ? 'Loading repo team members...'
                              : 'Search and select repo team member...'
                          }
                          disabled={isReadOnly || isLoadingFormData}
                          error={
                            !!(
                              errors.assignedOpsTeam && touched.assignedOpsTeam
                            )
                          }
                          className="w-full"
                          emptyMessage="No repo team members found"
                          allowClear
                        />
                      </div>
                      {errors.assignedOpsTeam && touched.assignedOpsTeam && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.assignedOpsTeam}
                        </p>
                      )}
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <div className="relative">
                        <textarea
                          value={formData.notes}
                          onChange={(e) =>
                            handleFieldChange('notes', e.target.value)
                          }
                          placeholder="Tambahkan detail kronologi"
                          rows={3}
                          maxLength={200}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 resize-none ${
                            isReadOnly
                              ? 'bg-gray-50 cursor-not-allowed'
                              : 'border-gray-300'
                          }`}
                        />
                        <div className="absolute bottom-3 right-2 text-xs text-gray-400 pointer-events-none">
                          {formData.notes.length}/200
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SideBarModal>
    </>
  );
};

export default RepoModal;
